import {
  darfStarten,
  markierKlasse,
  markierTag,
  markierTage,
  wirksamerMarkierTag,
} from '@netcon-work/shared'
import { useState } from 'react'
import { cn } from '../../lib/cn'
import { useNehmen, useStarten } from '../../lib/mutationen'
import { useAufgabenVerweis } from '../AufgabeKontext'
import { Fristwort } from './Fristwort'
import { WiedervorlageMarke } from './WiedervorlageMarke'
import type { Naechste } from './lage'
import { Play, Sun, UserPlus } from 'lucide-react'
import { Button, PrioBadge } from '../ui/primitives'
import { AufgabenWeltMarke, weltenGemischt } from '../WorkspaceMarke'
import { SonnenMarkeFuer } from '../Markierung'
import { SonnenKnopf } from './TelefonMarkieren'
import {
  heuteSchluessel,
  useDarfMarkieren,
  useMarkieren,
  useOffeneMarkierungen,
} from '../../lib/markierung'

/**
 * Wie viele Zeilen offen dastehen, bevor „weitere anzeigen" übernimmt.
 *
 * **Am Rechner drei** (Nils, 17.09.2026: *„Im Desktop hier sogar auf 3 kürzen, da kann man ja
 * «weitere anzeigen» drücken"*) – der Vorlauf steht dort über „Jetzt" und soll es nicht aus dem
 * Blick schieben. **Auf dem Telefon sieben** (16.09.2026): dort ist der Vorlauf ein eigener
 * Reiter und nimmt niemandem den Platz weg.
 */
export const VORLAUF_SICHTBAR = { rechner: 3, telefon: 7 } as const

/**
 * **Zone 1 des Modus „Einfach" – der Vorlauf**
 * ([Z-12](../../../../../docs/phasen/zusatz-12-mein-tag-einfach.md)).
 *
 * Dieselbe Rangfolge wie in `AlsNaechstes` und im Fenster „Was als Nächstes" – eine Liste, drei
 * Darstellungen ([Z-06](../../../../../docs/phasen/zusatz-06-nehmen-und-starten.md)). Neu ist
 * nicht *was* hier steht, sondern **wie herum**.
 *
 * ## Die Reihenfolge steht auf dem Kopf, und das ist der Punkt
 *
 * Nils, 28.08.2026: *„was kommt – was ist als nächstes dran (sortiert natürlich nach
 * wichtigkeit/als nächstes). das muss unten zu «jetzt» stehen."*
 *
 * Die Seite ist im Modus „Einfach" eine **Zeitachse**: oben die Zukunft, in der Mitte „Jetzt",
 * unten das Erledigte. Beide Ränder zeigen zur Mitte. Was als Nächstes dran ist, steht deshalb
 * **ganz unten**, direkt über „Jetzt" – von dort rückt es hinein. Nach oben hin wird es ferner,
 * kleiner und blasser.
 *
 * Das ist keine Spielerei, sondern das, was den Platz schafft: die oberen Zeilen kosten fast
 * nichts, weil sie nur Titel und Marke tragen. So stehen Zeilen da, wo vorher drei
 * gleich große Karten standen – Nils' eigentliche Bitte („mehr in Zone 1 sehen").
 *
 * ## Nur die unterste Zeile entscheidet
 *
 * Sie trägt „Nehmen" und „Starten" sichtbar; alles darüber ist **Ausblick**. Am Rechner kommen
 * die Knöpfe der oberen Zeilen beim Überfahren oder mit der Tastatur hervor, auf dem Telefon gar
 * nicht ([Z-10](../../../../../docs/phasen/zusatz-10-mobil.md)): unsichtbare Knöpfe, die man
 * versehentlich trifft, sind schlimmer als keine. Wer weiter oben zugreifen will, nimmt das
 * Fenster darunter – dort hat jede Zeile ihre Knöpfe **und** ihre Begründung.
 *
 * ## Was „Sofort" hier tut
 *
 * Nichts Besonderes – und genau das ist die Änderung. `nachGruppe` sortiert Sofort vor allem
 * anderen, also landet eine Störung von selbst auf der untersten Zeile und trägt dort die rote
 * Marke. Sie braucht dafür keine eigene Zone mehr über der Seite
 * ([E-270](../../../../../docs/ENTSCHEIDUNGEN.md#e-270)).
 */
export function Vorlauf({
  rangfolge,
  wip,
  wipLimit,
  fokusFuer,
  wipHinweisText,
  hinweis,
  fensterOeffnen,
  naechstesOben = false,
  kalenderOeffnen,
}: {
  rangfolge: readonly Naechste[]
  /** Der Stand der **aktiven** Welt – er trägt die Zeile „Fokus voll" unter der Liste. */
  wip: number
  wipLimit: number
  /**
   * **Die Grenze je Zeile** ([E-274](../../../../../docs/ENTSCHEIDUNGEN.md#e-274)).
   *
   * Seit der Vorrat über alle Arbeitswelten geht, stehen hier Zeilen aus mehreren Welten – und
   * `tasks.starten` rechnet je Aufgabe mit **ihrer** Welt. Ein eigenes ToDo gegen das Limit der
   * Entwicklung zu prüfen hieße, einen Knopf zu sperren, den der Server durchlässt.
   */
  fokusFuer: (workspaceId: string) => { wip: number; wipLimit: number }
  /** Woher das Limit kommt – nur gesetzt, wenn überhaupt eines gilt (Z-07.13). */
  wipHinweisText: string | null
  hinweis: string | null
  fensterOeffnen: () => void
  /**
   * **Das Nächste oben statt unten** – für das Telefon
   * ([Z-16](../../../../../docs/phasen/zusatz-16-pwa-mein-tag.md)).
   *
   * Die gedrehte Reihenfolge lebt von der Nachbarschaft zu „Jetzt": das Nächste steht direkt
   * über dem Laufenden, und der Blick wandert. Steht der Vorlauf in einem eigenen Reiter, gibt
   * es diese Nachbarschaft nicht – und die wichtigste Zeile läge hinter sechs anderen, bei
   * aufgeklappter Liste hinter allen. Dann zählt die Zahl hoch, der Aufklapper steht unten, und
   * die Naht entfällt. Am Rechner bleibt alles, wie es Z-12 gebaut hat.
   */
  naechstesOben?: boolean
  /**
   * **Den Kalender bei einem Tag öffnen** (Z-20) – aus der Ausblickzeile „Morgen 2 ·
   * Übermorgen 1". Fehlt er, steht die Zeile ohne Verweis da.
   */
  kalenderOeffnen?: (tag: string) => void
}) {
  const [alle, setAlle] = useState(false)
  const heute = heuteSchluessel()
  /** Für heute markiert – heute gesetzt oder liegengeblieben (F-95). */
  const heuteMarkiert = (a: Naechste) =>
    !!a.markiertAm && wirksamerMarkierTag(a.markiertAm, heute) === heute
  const gemischt = weltenGemischt(rangfolge)

  const anzahl = naechstesOben ? VORLAUF_SICHTBAR.telefon : VORLAUF_SICHTBAR.rechner
  const sichtbar = alle ? rangfolge : rangfolge.slice(0, anzahl)
  const rest = rangfolge.length - sichtbar.length
  /**
   * **Von hinten nach vorn gezeichnet.** Die Rangfolge selbst bleibt unangetastet – gedreht wird
   * nur die Darstellung. Der Rang steht als Zahl an der Zeile und zählt nach unten herunter:
   * `1` ist das, was als Nächstes dran ist.
   */
  const vonObenNachUnten = naechstesOben ? sichtbar : [...sichtbar].reverse()

  const aufklapper = (rest > 0 || alle) && (
    <button
      type="button"
      onClick={() => setAlle((a) => !a)}
      aria-expanded={alle}
      className={cn(
        'cursor-pointer text-[11.5px] text-muted hover:text-accent-deep hover:underline',
        naechstesOben ? 'mt-1 self-start' : 'mb-1',
      )}
    >
      {alle
        ? `▴ wieder auf ${naechstesOben ? 'sieben' : 'drei'} kürzen`
        : naechstesOben
          ? `▾ ${rest} weitere anzeigen`
          : `▴ ${rest} weitere anzeigen`}
    </button>
  )

  return (
    <section>
      <div className="mb-[9px] flex flex-wrap items-baseline gap-x-[9px] gap-y-1">
        <h3 className="text-[12px] font-bold tracking-[0.07em] uppercase">Vorlauf</h3>
        <span className="text-xs text-muted">
          {rangfolge.length === 0
            ? 'nichts offen und nichts im Vorrat'
            : naechstesOben
              ? 'was kommt – oben ist das Nächste'
              : 'was kommt – ganz unten ist das Nächste'}
        </span>
        {rangfolge.length > 0 && (
          <span className="tabular ml-auto text-[11px] text-muted">
            {rangfolge.length} {rangfolge.length === 1 ? 'Sache' : 'Sachen'}
          </span>
        )}
      </div>

      {/**
       * **Der Aufklapper steht oben**, weil dort das Ferne liegt. Er klappt an Ort und Stelle
       * auf: die ganze Rangfolge ist im Client gerechnet, ein Rundlauf wäre teurer als die
       * Zeilen, die er spart (Leitplanke 2).
       */}
      {!naechstesOben && aufklapper}

      {rangfolge.length === 0 ? (
        <p className="rounded-card border border-dashed border-line2 px-4 py-3 text-[12.5px] text-muted">
          Nichts offen und nichts im Vorrat. Der Tag gehört dem, was gerade läuft.
        </p>
      ) : (
        <div className="flex flex-col">
          {/**
           * **„☀ Heute vorgenommen" als Gruppe** (Z-20). Die markierten Zeilen stehen in der
           * Rangfolge hintereinander (`lage.ts`, direkt hinter Sofort) – sie hier zu einem
           * Abschnitt zusammenzufassen, ändert keine Reihenfolge, nur die Fläche dahinter.
           */}
          {abschnitte(vonObenNachUnten, heuteMarkiert).map(({ markiert, zeilen }) => {
            const inhalt = zeilen.map((a) => {
              /** Der Rang in der ungedrehten Liste – `0` ist das Nächste. */
              const platz = rangfolge.indexOf(a)
              return (
                <Zeile
                  key={a.id}
                  aufgabe={a}
                  platz={platz}
                  weltZeigen={gemischt}
                  fokus={fokusFuer(a.workspaceId)}
                />
              )
            })
            return markiert ? (
              <div
                key={`heute-${zeilen[0]!.id}`}
                data-heute-vorgenommen=""
                className="mb-1.5 rounded-xl border border-sonne-line bg-sonne-bg px-1.5 pt-1.5"
              >
                <p className="mb-1 flex items-center gap-1 px-1 text-[10.5px] font-bold tracking-[0.07em] text-sonne uppercase">
                  <Sun size={11} aria-hidden /> Heute vorgenommen
                  <span className="tabular ml-auto font-normal tracking-normal normal-case">
                    {rangfolge.filter(heuteMarkiert).length}
                  </span>
                </p>
                {inhalt}
              </div>
            ) : (
              inhalt
            )
          })}
        </div>
      )}

      {naechstesOben && aufklapper}

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={fensterOeffnen}
          title="Die ganze Reihenfolge – mit Begründung je Vorschlag."
        >
          Alle {rangfolge.length} ansehen
        </Button>

        {/*
          Sichtbar und nicht nur im `title`: der Grund, warum die Starten-Knöpfe grau sind,
          gehört auf den Bildschirm. Ein Tooltip sieht nur, wer schwebt – wer klickt, sieht
          nichts und hält die Ansicht für kaputt.
        */}
        {Number.isFinite(wipLimit) && wip >= wipLimit && (
          <p className="text-[11.5px] font-semibold text-warn">
            Fokus voll ({wip} von {wipLimit}) – Starten ist gesperrt, Nehmen nicht. 🔥 Sofort geht
            trotzdem.
            {wipHinweisText && <span className="block font-normal">{wipHinweisText}</span>}
          </p>
        )}
        {hinweis && <p className="text-[11.5px] text-muted">{hinweis}</p>}
      </div>

      <Ausblick heute={heute} kalenderOeffnen={kalenderOeffnen} />

      {/**
       * **Die Naht zwischen Vorlauf und „Jetzt".** Ohne sie wären es zwei Listen untereinander;
       * mit ihr ist es eine Bewegung. Rein zierend und deshalb `aria-hidden` – wer vorliest,
       * hat die Überschriften.
       */}
      {rangfolge.length > 0 && !naechstesOben && (
        <div
          className="flex items-center gap-2 pt-2.5 pb-0.5 text-[11px] text-muted"
          aria-hidden="true"
        >
          <span className="h-px flex-1 bg-gradient-to-r from-transparent to-line2" />
          rückt nach, sobald ein Platz frei wird
          <span className="h-px flex-1 bg-gradient-to-l from-transparent to-line2" />
        </div>
      )}
    </section>
  )
}

/**
 * Eine Zeile im Vorlauf. Wie kräftig sie auftritt, entscheidet allein `platz` – die Stufe ist
 * die Entfernung zu „Jetzt", nicht die Priorität. Ein A-Ticket auf Rang 6 ist heute nicht dran.
 */
function Zeile({
  aufgabe: a,
  platz,
  weltZeigen,
  fokus,
}: {
  aufgabe: Naechste
  platz: number
  weltZeigen: boolean
  /** Stand und Grenze **der Welt dieser Aufgabe** – siehe `fokusFuer` oben. */
  fokus: { wip: number; wipLimit: number }
}) {
  const verweis = useAufgabenVerweis(a.id)
  const nehmen = useNehmen()
  const starten = useStarten()
  const [grund, setGrund] = useState('')
  const [fragtNachGrund, setFragtNachGrund] = useState(false)

  /**
   * **Wünscht sich die Aufgabe jemand anders?** (21.09.2026, aus der Review-Aufgabe.)
   *
   * Dann verlangt der Server einen Grund (Phase 8, Nils am 04.08.2026: *„jeder soll sich die
   * trotzdem holen können, dann mit einer Abfrage: Sicher dass du die Aufgabe nehmen willst
   * (Grund zwingend angeben)"*). **Gefragt hat die Oberfläche nie** – sie schickte den Aufruf
   * ohne Grund, der Server wies ihn ab, und `onError` rollte stumm zurück. Für den Anwender
   * hieß das: *„Beim Klick auf den Button passiert nichts."* Davin hat es am 21.09. dreimal
   * hintereinander versucht, im Protokoll dreimal dieselbe Absage.
   *
   * Gefragt wird **vorher**, nicht nach der Absage: Der Client kennt die Wünsche längst. Ein
   * Aufruf, der absehbar scheitert, ist eine Runde zum Server, die niemandem hilft.
   */
  const grundNoetig = a.wunschIds.length > 0 && a.marke !== 'vorgemerkt' && a.marke !== 'bei_dir'

  const naechste = platz === 0
  const stufe = Math.min(platz, 3)
  const eigen = a.marke === 'bei_dir'
  const sofort = a.pool?.wipDurchbrechen ?? false
  const pruefung = darfStarten({
    inArbeit: fokus.wip,
    wipLimit: fokus.wipLimit,
    poolDurchbrichtWip: sofort,
  })

  return (
    <div
      data-vorlauf={a.id}
      data-platz={platz}
      className={cn(
        'group flex flex-wrap items-center gap-x-2 gap-y-1 rounded-xl border bg-surface transition-colors',
        naechste
          ? cn(
              'border-line2 px-[15px] py-3 shadow-task',
              sofort ? 'border-l-[3px] border-l-pool-sofort' : 'border-l-[3px] border-l-accent',
            )
          : 'mb-1.5 border-line px-3',
        stufe === 1 && 'py-[7px] opacity-90',
        stufe === 2 && 'py-1.5 opacity-75',
        stufe === 3 && 'py-1 opacity-[0.55]',
      )}
    >
      {/**
       * **Die Zahl zählt herunter, nicht hoch.** „3 · 2 · 1" über „Jetzt" liest sich als
       * Anflug; „1 · 2 · 3" wäre eine Liste, die von der Arbeit wegführt.
       */}
      <span
        className={cn(
          'tabular w-4 shrink-0 text-right text-[10.5px] font-bold text-muted',
          naechste && 'text-accent-deep',
        )}
        aria-hidden="true"
      >
        {platz + 1}
      </span>

      {naechste && (
        <span className="rounded-[5px] bg-accent-soft px-[7px] py-[2px] text-[10px] font-bold tracking-[0.08em] text-accent-deep uppercase">
          Als Nächstes
        </span>
      )}

      {a.priority && <PrioBadge priority={a.priority} />}

      {a.wiedervorlage && <WiedervorlageMarke tag={a.wiedervorlage} />}

      {a.markiertAm && <SonnenMarkeFuer am={a.markiertAm} klein />}

      {/**
       * **„Das sieht gerade niemand an"** (Z-19 AP 19.10). Ohne Zeitfenster, ohne Frist, ohne
       * Wiedervorlage und seit zwei Wochen unberührt – die Zeile steht zwar hier, aber ganz
       * hinten, und niemandem fällt sie je wieder ins Auge. Genau davor war die Angst, die zu
       * zwölf „in Arbeit" geführt hat.
       */}
      {a.uebersehen && (
        <span
          className="shrink-0 rounded-full border border-[#f0d9a8] bg-[#fdf3dc] px-[6px] py-px text-[10px] font-semibold text-[#8a6100]"
          title="Ohne Zeitfenster, ohne Frist, ohne Wiedervorlage und seit über zwei Wochen unberührt – so begegnet sie niemandem mehr."
        >
          übersehen
        </span>
      )}

      {/**
       * **Ein echter Verweis, kein Knopf** ([E-319](../../../../../docs/ENTSCHEIDUNGEN.md#e-319)).
       * Strg+Klick und mittlere Maustaste öffnen damit einen neuen Tab, ohne dass wir eine
       * einzige Taste abfragen müssten.
       */}
      <a
        {...verweis}
        title={a.title}
        className={cn(
          'min-w-0 flex-1 cursor-pointer truncate text-left hover:text-accent hover:underline',
          /**
           * **Auf dem Telefon eine eigene, volle Zeile** (Z-16, nach Nils' Bildschirmfoto vom
           * 14.09.): mit `flex-1` und Basis 0 bekam der Titel nur, was Rang, Marke, Pool- und
           * Vorhaben-Chip übrig ließen – auf 390 px eine Spalte von 70 px, ein Wort je Zeile,
           * jedes mit Auslassungspunkten. `basis-full` nimmt ihm den Kampf um die Zeile ab:
           * Rang und Marke oben, der Titel darunter in voller Breite, die Chips danach.
           */
          naechste
            ? 'text-[14px] font-bold tracking-[-0.1px] max-sm:basis-full max-sm:whitespace-normal max-sm:text-[16px] max-sm:leading-snug'
            : /**
               * **Auf dem Telefon auch hier eine eigene Zeile** (Z-20, 05.10.2026): seit der
               * ☀-Knopf dazukam, blieb dem Titel neben Marke, Frist und zwei Knöpfen nichts mehr –
               * „ND…", „Anf…". Derselbe Griff wie an der untersten Zeile (Z-16).
               */
              'text-[12.5px] max-sm:basis-full max-sm:whitespace-normal max-sm:text-[14px] max-sm:leading-snug',
          stufe === 3 && 'text-[12px]',
        )}
      >
        {a.title}
      </a>

      {/* Die Marke sagt, welcher Knopf gilt: ● gehört dir → nur Starten, sonst Nehmen. */}
      <span
        className={cn(
          'max-w-full shrink-0 truncate rounded-md px-[7px] py-[2px] text-[10.5px] font-semibold',
          eigen
            ? 'bg-accent-soft text-accent-deep'
            : sofort
              ? 'bg-pool-sofort-bg text-pool-sofort'
              : 'bg-pool-tickets-bg text-pool-tickets',
        )}
        title={
          eigen
            ? 'Gehört dir – nur noch anfangen.'
            : a.pool
              ? (a.pool.regeln ?? undefined)
              : 'Die Quelle führt dich als Bearbeiter, in Work hat sie noch niemand gezogen.'
        }
      >
        {/**
         * **Eine Zeile ohne Pool** ([E-316](../../../../../docs/ENTSCHEIDUNGEN.md#e-316),
         * 04.09.2026): was die Quelle mir zuschreibt, aber in keinem Vorrat liegt. Ohne diesen
         * Zweig stünde hier „undefined undefined" – der Fall gab es bis heute nicht.
         */}
        {eigen
          ? '● gehört dir'
          : a.pool
            ? `${a.pool.icon} ${a.pool.label}`
            : a.marke === 'herrenlos'
              ? /** Z-20: von mir markiert, liegt aber in keinem Vorrat und bei niemandem für mich. */
                '☀ von dir markiert'
              : '◐ dir zugeschrieben'}
      </span>

      {a.vorhabenLabel && (
        <span
          className="max-w-[40%] shrink-0 truncate rounded-md bg-vorhaben-bg px-[7px] py-[2px] text-[10.5px] font-semibold text-pool-projekte"
          title={a.vorhabenLabel}
        >
          ◆ {a.vorhabenLabel}
        </span>
      )}

      {/**
       * **Die Frist steht an jeder Zeile** (Nils, 02.09.2026: *„Im Vorlauf soll bei allen Sachen
       * das errechnete Fällig stehen. Nicht nur bei der akutesten"*).
       *
       * Bis hierher stand die ganze Nebenauskunft nur an der untersten Zeile, mit der
       * Begründung, weiter oben sei sie *„eine Antwort auf eine Frage, die dort noch niemand
       * stellt"*. Für ★ und „liegt bei" stimmt das weiterhin – für den **Termin** nicht: er ist
       * der Grund, warum eine Zeile weiter oben oder weiter unten steht, und ohne ihn muss man
       * die Reihenfolge glauben, statt sie zu sehen.
       *
       * Oben deshalb **nur die Frist** (`nurFrist`), unten die vollständige Zeile. Die Herkunft
       * des Datums hängt an beiden im Tooltip ([E-308](../../../../../docs/ENTSCHEIDUNGEN.md#e-308)).
       */}
      {naechste ? (
        <span className="flex w-full flex-wrap items-center gap-x-2 text-[10.5px] text-muted sm:w-auto">
          <AufgabenWeltMarke workspaceId={a.workspaceId} zeigen={weltZeigen} />
          <span>
            <Fristwort frist={a.frist} zeitfenster={a.zeitfenster} herkunft={a.fristHerkunft} />
            {a.wunschIds.length > 0 ? ` · ★ ${a.wunschNamen}` : ''}
            {!eigen && a.bearbeiterName ? ` · liegt bei ${a.bearbeiterName}` : ''}
          </span>
        </span>
      ) : (
        <span className="shrink-0 text-[10.5px] text-muted">
          <Fristwort
            frist={a.frist}
            zeitfenster={a.zeitfenster}
            herkunft={a.fristHerkunft}
            nurFrist
          />
        </span>
      )}

      {/**
       * **Am Rechner: Knöpfe nur unten, oben erst beim Hinsehen** (Z-12.3). Ein unsichtbarer
       * Knopf an der Stelle, wo man tippt, um eine Aufgabe zu öffnen, wäre eine Falle – deshalb
       * standen die oberen Zeilen auf dem Telefon bis zum 14.09.2026 ganz ohne Knopf.
       *
       * **Auf dem Telefon: sichtbar, als Symbol** (Z-16, Nils: *„Wie würde ich denn da die
       * starten? Wenn ich klicke öffnet sich ja der Dialog"*). Kein Schweben heißt nicht kein
       * Knopf, sondern ein **sichtbarer**: ▶ Starten und, bei fremden Zeilen, ⊕ Nehmen – klein,
       * am rechten Rand, 40 px hoch (`tap`). Der Titel bleibt der Weg ins Detail.
       */}
      <span
        className={cn(
          'ml-auto flex shrink-0 gap-1',
          !naechste &&
            'opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 max-sm:opacity-100',
        )}
      >
        <MarkierMini aufgabe={a} />
        {!eigen && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              if (grundNoetig && !fragtNachGrund) {
                setFragtNachGrund(true)
                return
              }
              nehmen.mutate(grund.trim() ? { id: a.id, grund: grund.trim() } : { id: a.id })
            }}
            aria-label={`${a.title} nehmen`}
            className={cn(!naechste && 'max-sm:px-2.5')}
          >
            <span className={cn(!naechste && 'max-sm:hidden')}>Nehmen</span>
            {!naechste && <UserPlus aria-hidden="true" className="size-4 sm:hidden" />}
          </Button>
        )}
        <Button
          size="sm"
          variant={naechste ? 'primary' : 'ghost'}
          disabled={!pruefung.erlaubt}
          onClick={() => starten.mutate({ id: a.id })}
          aria-label={`${a.title} starten`}
          className={cn(!naechste && 'max-sm:px-2.5')}
          {...(pruefung.grund ? { title: pruefung.grund } : {})}
        >
          <span className={cn(!naechste && 'max-sm:hidden')}>Starten</span>
          {!naechste && <Play aria-hidden="true" className="size-4 sm:hidden" />}
        </Button>
      </span>

      {fragtNachGrund && (
        <div className="mt-2 w-full rounded-lg bg-[#fdf3dc] px-2.5 py-2 text-[12px]">
          <p className="text-[#8a6100]">
            {a.wunschNamen
              ? `Diese Aufgabe wünscht sich ${a.wunschNamen}.`
              : 'Diese Aufgabe wünscht sich jemand anders.'}{' '}
            Du kannst sie trotzdem nehmen – mit einem kurzen Grund.
          </p>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <input
              value={grund}
              autoFocus
              onChange={(e) => setGrund(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && grund.trim())
                  nehmen.mutate({ id: a.id, grund: grund.trim() })
                if (e.key === 'Escape') setFragtNachGrund(false)
              }}
              placeholder="z. B. „Chris ist im Urlaub“"
              aria-label="Grund für das Nehmen"
              className="min-w-0 flex-1 rounded-md border border-[#eccf8a] bg-surface px-2 py-1 outline-none focus:border-[#a04a12]"
            />
            <Button
              size="sm"
              variant="primary"
              disabled={!grund.trim() || nehmen.isPending}
              onClick={() => nehmen.mutate({ id: a.id, grund: grund.trim() })}
            >
              Trotzdem nehmen
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setFragtNachGrund(false)}>
              Abbrechen
            </Button>
          </div>
        </div>
      )}

      {/**
       * **Rollback **und** Hinweis** – Leitplanke 1 verlangt beides, geliefert wurde bisher nur
       * das Rollback. Eine Aktion, die lautlos nichts tut, ist schlimmer als eine, die sich
       * beschwert: Man weiß nicht, ob es klemmt oder ob man selbst etwas falsch macht.
       */}
      {(nehmen.error || starten.error) && !fragtNachGrund && (
        <p role="alert" className="mt-1.5 w-full text-[12px] text-[#b02a2a]">
          {(nehmen.error ?? starten.error)?.message}
        </p>
      )}
    </div>
  )
}

/** Zerlegt die gezeichnete Liste in Abschnitte: zusammenhängende markierte Zeilen bilden einen. */
function abschnitte(
  zeilen: readonly Naechste[],
  markiert: (a: Naechste) => boolean,
): { markiert: boolean; zeilen: Naechste[] }[] {
  const ergebnis: { markiert: boolean; zeilen: Naechste[] }[] = []
  for (const z of zeilen) {
    const m = markiert(z)
    const letzter = ergebnis.at(-1)
    if (letzter && letzter.markiert === m) letzter.zeilen.push(z)
    else ergebnis.push({ markiert: m, zeilen: [z] })
  }
  return ergebnis
}

/**
 * **☀ Heute · Morgen** an der Zeile (Z-20) – markieren, ohne die Aufgabe zu öffnen.
 *
 * Zwei Knöpfe, nicht drei: im Vorlauf ist die Frage „heute oder bald", für alles Weitere gibt es
 * Detail und Kalender. Ein Klick auf den gewählten Tag nimmt die Markierung wieder weg. Sichtbar
 * wie die übrigen Knöpfe der Zeile – unten immer, darüber beim Hinsehen.
 */
function MarkierMini({ aufgabe: a }: { aufgabe: Naechste }) {
  const darf = useDarfMarkieren()
  const markieren = useMarkieren()
  if (!darf) return null
  const heute = heuteSchluessel()
  const klasse = a.markiertAm ? markierKlasse(a.markiertAm, heute) : null
  const knopf = (stufe: 'heute' | 'morgen', text: string) => {
    const aktiv = klasse === stufe
    return (
      <Button
        size="sm"
        variant="ghost"
        aria-pressed={aktiv}
        title={aktiv ? 'Markierung entfernen' : `Für ${text} markieren`}
        aria-label={`${a.title} für ${text} markieren`}
        onClick={() =>
          markieren.mutate({
            taskIds: [a.id],
            am: aktiv ? null : stufe === 'heute' ? heute : markierTag('morgen', heute),
          })
        }
        /**
         * **Auch an der untersten Zeile erst beim Hinsehen** (05.10.2026, im Browser gesehen):
         * dort drängten die zwei Knöpfe neben „Starten" den Titel auf „Ände…" zusammen. Was
         * gesetzt ist, sagt ohnehin die Sonne an der Zeile.
         */
        className={cn(
          'max-sm:hidden sm:opacity-0 sm:group-focus-within:opacity-100 sm:group-hover:opacity-100',
          aktiv && 'bg-sonne-bg text-sonne',
        )}
      >
        {stufe === 'heute' && <Sun aria-hidden="true" className="size-3.5" />}
        {text}
      </Button>
    )
  }
  return (
    <>
      {knopf('heute', 'Heute')}
      {knopf('morgen', 'Morgen')}
      {/** Auf dem Telefon ein Knopf mit Blatt statt zweier schmaler (Z-20, AP 20.7). */}
      <span className="sm:hidden">
        <SonnenKnopf taskId={a.id} titel={a.title} />
      </span>
    </>
  )
}

/**
 * **„Danach: Morgen 2 · Übermorgen 1 · später 1"** – der Blick über heute hinaus (Z-20).
 *
 * Aus allen meinen Markierungen, nicht aus der Rangfolge: was für morgen markiert ist, steht
 * heute (noch) nicht im Vorlauf, und genau das soll man hier sehen. Antippen öffnet den Kalender
 * bei diesem Tag, wo man umplanen kann.
 */
function Ausblick({
  heute,
  kalenderOeffnen,
}: {
  heute: string
  kalenderOeffnen: ((tag: string) => void) | undefined
}) {
  const markierungen = useOffeneMarkierungen()
  const tage = markierTage(heute).slice(1)
  const zahlen = tage.map(({ stufe, tag }) => ({
    stufe,
    tag,
    anzahl: [...markierungen.values()].filter((am) => markierKlasse(am, heute) === stufe).length,
  }))
  const spaeter = [...markierungen.values()].filter((am) => markierKlasse(am, heute) === 'spaeter')
  if (zahlen.every((z) => z.anzahl === 0) && spaeter.length === 0) return null

  const eintrag = (text: string, anzahl: number, tag: string) =>
    kalenderOeffnen ? (
      <button
        key={text}
        type="button"
        onClick={() => kalenderOeffnen(tag)}
        className="tap cursor-pointer rounded-full border border-line bg-surface px-2 py-px hover:border-sonne-line hover:text-sonne"
      >
        {text} <span className="tabular font-semibold">{anzahl}</span>
      </button>
    ) : (
      <span key={text}>
        {text} <span className="tabular font-semibold">{anzahl}</span>
      </span>
    )

  return (
    <p className="mt-2 flex flex-wrap items-center gap-1.5 text-[11.5px] text-ink2">
      <Sun size={11} className="text-sonne" aria-hidden />
      <span className="text-muted">Danach markiert:</span>
      {zahlen.map(({ stufe, tag, anzahl }) =>
        eintrag(stufe === 'morgen' ? 'Morgen' : 'Übermorgen', anzahl, tag),
      )}
      {spaeter.length > 0 && eintrag('später', spaeter.length, [...spaeter].sort()[0]!)}
    </p>
  )
}
